import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  App, Card, Descriptions, Table, Button, Spin, Result, Space, Tag, Row, Col,
} from 'antd';
import { ArrowLeftOutlined, StopOutlined, InboxOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import { formatCurrency } from '../utils/format';

interface OrderItem {
  id: string;
  title: string;
  thumbnail?: string;
  sku?: string;
  quantity: number;
  unit_price: number;
  total: number;
  variant?: { title: string };
}

interface OrderDetailData {
  id: string;
  display_id: number;
  email: string;
  customer_id?: string;
  status: string;
  payment_status: string;
  fulfillment_status: string;
  currency_code: string;
  subtotal: number;
  discount_total: number;
  shipping_total: number;
  tax_total: number;
  total: number;
  items: OrderItem[];
  customer?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
  };
  shipping_address?: {
    address_1: string;
    address_2?: string;
    city: string;
    province?: string;
    postal_code: string;
    country_code: string;
  };
  billing_address?: {
    address_1: string;
    address_2?: string;
    city: string;
    province?: string;
    postal_code: string;
    country_code: string;
  };
  payment_collections?: Array<{
    id: string;
    status: string;
    amount: number;
  }>;
  fulfillments?: Array<{
    id: string;
    status: string;
    items: Array<{ item_id: string; quantity: number }>;
  }>;
  created_at: string;
}

const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { message, modal } = App.useApp();
  const [order, setOrder] = useState<OrderDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadOrder();
  }, [id]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/admin/orders/${id}`, {
        params: {
          fields:
            'id,display_id,email,customer_id,status,payment_status,fulfillment_status,currency_code,subtotal,discount_total,shipping_total,tax_total,total,created_at,*items,*customer,*shipping_address,*billing_address,*payment_collections,*fulfillments',
        },
      });
      const orderData = response.data.order || response.data;
      if (!orderData.customer && orderData.customer_id) {
        const customerResponse = await apiClient
          .get(`/admin/customers/${orderData.customer_id}`)
          .catch(() => null);
        if (customerResponse) {
          orderData.customer = customerResponse.data.customer || customerResponse.data;
        }
      }
      setOrder(orderData);
    } catch (error: any) {
      if (error.response?.status === 404) {
        setNotFound(true);
      } else {
        message.error(error.response?.data?.message || t('common.noData'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    modal.confirm({
      title: t('orders.cancel'),
      icon: <ExclamationCircleOutlined />,
      content: t('common.confirm'),
      okText: t('common.yes'),
      cancelText: t('common.no'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          setActionLoading(true);
          await apiClient.post(`/admin/orders/${id}/cancel`);
          message.success(t('orders.canceledSuccess'));
          loadOrder();
        } catch (error: any) {
          message.error(error.response?.data?.message || t('orders.cancelError'));
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleArchive = async () => {
    try {
      setActionLoading(true);
      await apiClient.post(`/admin/orders/${id}/archive`);
      message.success(t('orders.archiveSuccess'));
      loadOrder();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('orders.archiveError'));
    } finally {
      setActionLoading(false);
    }
  };

  const formatAddress = (addr: any) => {
    if (!addr) return '';
    const parts = [addr.address_1, addr.address_2, addr.postal_code, addr.city, addr.province];
    if (!parts.filter(Boolean).length) return t('common.noAddress');
    return [...parts, addr.country_code].filter(Boolean).join(', ');
  };

  const getStatusLabel = (status: string) => {
    const map: Record<string, string> = {
      pending: t('orders.statusPending'),
      completed: t('orders.statusCompleted'),
      canceled: t('orders.statusCanceled'),
      archived: t('orders.statusArchived'),
    };
    return map[status] || status;
  };

  const getStatusColor = (status: string) => {
    const map: Record<string, string> = {
      completed: 'green',
      canceled: 'red',
      archived: 'default',
    };
    return map[status] || 'blue';
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (notFound || !order) {
    return (
      <Result
        status="404"
        title="404"
        subTitle={t('common.noData')}
        extra={
          <Button type="primary" onClick={() => navigate('/orders')}>
            {t('common.back')}
          </Button>
        }
      />
    );
  }

  const currency = order.currency_code || 'EUR';

  const itemColumns = [
    {
      title: t('products.name'),
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: t('quotes.sku'),
      dataIndex: 'sku',
      key: 'sku',
      render: (sku: string) => sku || '-',
    },
    {
      title: t('quotes.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: t('quotes.unitPrice'),
      dataIndex: 'unit_price',
      key: 'unit_price',
      render: (v: number) => formatCurrency(v, currency, i18n.language),
    },
    {
      title: t('orders.totalInclVat'),
      dataIndex: 'total',
      key: 'total',
      render: (v: number) => formatCurrency(v, currency, i18n.language),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/orders')}>
          {t('common.back')}
        </Button>
      </Space>

      <Card
        title={
          <Space>
            <span>{t('orders.detail')} - {order.display_id || order.id}</span>
            <Tag color={getStatusColor(order.status)}>
              {getStatusLabel(order.status)}
            </Tag>
          </Space>
        }
        extra={
          <Space>
            {order.status !== 'canceled' && order.status !== 'completed' && (
              <Button
                danger
                icon={<StopOutlined />}
                onClick={handleCancel}
                loading={actionLoading}
              >
                {t('orders.cancel')}
              </Button>
            )}
            <Button
              icon={<InboxOutlined />}
              onClick={handleArchive}
              loading={actionLoading}
            >
              {t('orders.archive')}
            </Button>
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Row gutter={24}>
          <Col xs={24} lg={16}>
            <Card type="inner" title={t('orders.items')} style={{ marginBottom: 16 }}>
              <Table
                columns={itemColumns}
                dataSource={order.items || []}
                rowKey="id"
                pagination={false}
                size="small"
              />

              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <Descriptions column={1} size="small" style={{ minWidth: 250 }}>
                  <Descriptions.Item label={t('quotes.subtotal')}>
                    {formatCurrency(order.subtotal, currency, i18n.language)}
                  </Descriptions.Item>
                  {(order.discount_total || 0) > 0 && (
                    <Descriptions.Item label={t('quotes.discounts')}>
                      -{formatCurrency(order.discount_total, currency, i18n.language)}
                    </Descriptions.Item>
                  )}
                  {(order.shipping_total || 0) > 0 && (
                    <Descriptions.Item label={t('quotes.shipping')}>
                      {formatCurrency(order.shipping_total, currency, i18n.language)}
                    </Descriptions.Item>
                  )}
                  {(order.tax_total || 0) > 0 && (
                    <Descriptions.Item label={t('quotes.tax')}>
                      {formatCurrency(order.tax_total, currency, i18n.language)}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label={t('common.total')}>
                    <span style={{ fontWeight: 600, color: '#BD7423' }}>
                      {formatCurrency(order.total, currency, i18n.language)}
                    </span>
                  </Descriptions.Item>
                </Descriptions>
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card type="inner" title={t('orders.customer')} style={{ marginBottom: 16 }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label={t('common.name')}>
                  {[order.customer?.first_name, order.customer?.last_name].filter(Boolean).join(' ').trim() || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('common.email')}>
                  {order.customer?.email || order.email || '-'}
                </Descriptions.Item>
                {order.customer?.phone ? (
                  <Descriptions.Item label={t('common.phone')}>
                    {order.customer.phone}
                  </Descriptions.Item>
                ) : null}
              </Descriptions>
            </Card>

            <Card type="inner" title={t('orders.paymentStatus')} style={{ marginBottom: 16 }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label={t('orders.paymentStatus')}>
                  <Tag color={order.payment_status === 'captured' ? 'green' : 'orange'}>
                    {order.payment_status === 'captured' ? t('orders.paymentCaptured') :
                     order.payment_status === 'awaiting' ? t('orders.paymentAwaiting') :
                     order.payment_status === 'not_paid' ? t('orders.paymentNotPaid') :
                     order.payment_status === 'refunded' ? t('orders.paymentRefunded') :
                     order.payment_status}
                  </Tag>
                </Descriptions.Item>
                {order.payment_collections?.map((pc) => (
                  <Descriptions.Item key={pc.id} label={t('orders.amount')}>
                    {formatCurrency(pc.amount, currency, i18n.language)}
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </Card>

            <Card type="inner" title={t('orders.fulfillmentStatus')} style={{ marginBottom: 16 }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label={t('orders.fulfillmentStatus')}>
                  <Tag color={order.fulfillment_status === 'fulfilled' ? 'green' : 'default'}>
                    {order.fulfillment_status === 'fulfilled' ? t('orders.fulfilled') : t('orders.notFulfilled')}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {formatAddress(order.shipping_address) && (
              <Card type="inner" title={t('orders.shippingAddress')} style={{ marginBottom: 16 }}>
                <p>{formatAddress(order.shipping_address)}</p>
              </Card>
            )}

            {formatAddress(order.billing_address) && (
              <Card type="inner" title={t('orders.billingAddress')} style={{ marginBottom: 16 }}>
                <p>{formatAddress(order.billing_address)}</p>
              </Card>
            )}
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default OrderDetail;
